import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApiInterface } from '../../../core/interfaces';
import { NotificationStatusEnum } from '../enums/notification-type.enum';
import {
  NotificationInterface,
  NotificationPageInterface,
  NotificationPreferenceInterface
} from '../interfaces/notification.interface';

@Injectable({
  providedIn: 'root'
})
export class NotificationsApiService {
  private readonly API_URL = `${environment.url}/api/v2/notifications`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches notifications with pagination.
   */
  getNotifications(
    page = 1,
    limit = 10,
    status?: NotificationStatusEnum
  ): Observable<NotificationPageInterface> {
    let params = new HttpParams().set('page', page).set('limit', limit);
    if (status) params = params.set('status', status);

    return this.http
      .get<ResponseApiInterface<NotificationInterface[]>>(this.API_URL, { params })
      .pipe(
        map((response) => ({
          notifications: response.data ?? [],
          meta: this.requireMeta(response)
        }))
      );
  }

  /**
   * Fetches the count of unread notifications.
   */
  getUnreadCount(): Observable<{ count: number }> {
    return this.http
      .get<ResponseApiInterface<{ count: number }>>(`${this.API_URL}/unread-count`)
      .pipe(map((response) => response.data ?? { count: 0 }));
  }

  /**
   * Marks a specific notification as read.
   */
  markAsRead(id: string): Observable<NotificationInterface> {
    return this.http
      .patch<ResponseApiInterface<NotificationInterface>>(`${this.API_URL}/${id}/read`, {})
      .pipe(map((response) => this.requireData(response)));
  }

  /**
   * Marks all notifications as read.
   */
  markAllAsRead(): Observable<number> {
    return this.http
      .patch<ResponseApiInterface<{ updatedCount: number }>>(
        `${this.API_URL}/mark-all-as-read`,
        {}
      )
      .pipe(map((response) => response.data?.updatedCount ?? 0));
  }

  getPreferences(): Observable<NotificationPreferenceInterface[]> {
    return this.http
      .get<ResponseApiInterface<{ preferences: NotificationPreferenceInterface[] }>>(
        `${this.API_URL}/preferences`
      )
      .pipe(map((response) => response.data?.preferences ?? []));
  }

  updatePreferences(
    preferences: NotificationPreferenceInterface[]
  ): Observable<NotificationPreferenceInterface[]> {
    return this.http
      .put<ResponseApiInterface<{ preferences: NotificationPreferenceInterface[] }>>(
        `${this.API_URL}/preferences`,
        { preferences: preferences.map(({ eventKey, channel, enabled }) => ({ eventKey, channel, enabled })) }
      )
      .pipe(map((response) => response.data?.preferences ?? []));
  }

  private requireData(response: ResponseApiInterface<NotificationInterface>): NotificationInterface {
    if (!response.data) throw new Error('A resposta da notificação está incompleta.');
    return response.data;
  }

  private requireMeta(response: ResponseApiInterface<NotificationInterface[]>) {
    if (!response.meta) throw new Error('A paginação das notificações está incompleta.');
    return response.meta;
  }
}
